import type { StoryShot } from "../components/ux/StoryWheel";
import type { QuizAudience } from "../lib/quizAudience";

/**
 * The story photo/clip library, shared by the story lander and the onboarding
 * flow's reveal screen.
 *
 * Media is glob-imported: dropping a new `story_s_*` / `story_v_*` set into
 * the asset folders is enough to build another pack. A card falls back to its
 * still whenever a clip hasn't been produced yet (and always under
 * reduced-motion).
 *
 * Packs are keyed by prefix: "" is the sapphic set, "m" younger men, "mm"
 * men 40+, "wm" women 40+.
 */

const STORY_IMAGES = import.meta.glob<string>(
  "../assets/images/story/*.webp",
  { eager: true, import: "default" }
);
const STORY_VIDEOS = import.meta.glob<string>(
  "../assets/videos/story/*.mp4",
  { eager: true, import: "default" }
);

export function storyImg(name: string): string {
  const url = STORY_IMAGES[`../assets/images/story/story_s_${name}.webp`];
  if (!url) throw new Error(`Missing story image: story_s_${name}.webp`);
  return url;
}

export function storyShot(name: string): StoryShot {
  return {
    img: storyImg(name),
    video: STORY_VIDEOS[`../assets/videos/story/story_v_${name}.mp4`],
  };
}

/** Card order. Pride and cafe shots are spaced so neither clusters as the disc turns. */
export const STORY_CARDS = [
  "group", "talking", "pride", "table", "pair", "outside", "cafe", "trio",
];

export type StoryPackPrefix = "" | "m" | "mm" | "wm";

/**
 * Cards that are the same table, photographed twice.
 *
 * Each pack was generated with a recurring cast, so a few cards are two shots
 * of one night rather than two nights. That is fine on the wheel, which just
 * turns, and a problem on the intro stack, where every print carries a
 * handwritten caption naming a different city: the same face under "silver
 * lake" and "nyc" tells the reader these nights are staged, on the one screen
 * whose whole job is to look like other people's real evenings.
 *
 * Checked pack by pack, card by card. Only these repeat today — men's `group`
 * and `pride` share their foreground man, and the 40+ men's pair is the same
 * four people around the same table. Add a group here when a new pack lands
 * with a shared cast; the intro reads it and takes only the first of each.
 */
const SAME_TABLE: Partial<Record<StoryPackPrefix, readonly (readonly string[])[]>> = {
  m: [["group", "pride"]],
  mm: [["group", "pride"]],
};

/**
 * What a card counts as for "have we shown this table already?".
 *
 * Resolved against every pack in play rather than the one card's own pack: on
 * a mixed wheel the packs alternate by position, and a filter that shifts
 * positions would change which pack each card came from as it ran.
 */
function tableKey(packs: StoryPackPrefix[], name: string): string {
  for (const prefix of packs) {
    const group = (SAME_TABLE[prefix] ?? []).find((g) => g.includes(name));
    if (group) return `${prefix}:${group[0]}`;
  }
  return name;
}

export function storyPack(
  prefix: StoryPackPrefix,
  names: string[] = STORY_CARDS,
): StoryShot[] {
  const p = prefix ? `${prefix}_` : "";
  return names.map((n) => storyShot(`${p}${n}`));
}

/**
 * Alternates two packs so a mixed audience sees a mixed wheel, rather than one
 * community's set standing in for everyone. Cards keep the `STORY_CARDS`
 * ordering, so pride and cafe shots stay spaced apart on the disc.
 */
export function interleavePacks(
  a: StoryPackPrefix,
  b: StoryPackPrefix,
  names: string[] = STORY_CARDS,
): StoryShot[] {
  return names.map((name, i) => {
    const prefix = i % 2 === 0 ? a : b;
    return storyShot(prefix ? `${prefix}_${name}` : name);
  });
}

/**
 * Which packs an audience sees. `a=m` and `a=s` come off the ad landing, so
 * this is the difference between a sapphic visitor being shown sapphic nights
 * and being shown a stock mix.
 */
export function packsForAudience(
  audience: QuizAudience,
  age?: number | null,
): StoryPackPrefix[] {
  // The library splits every community at 40 — `mm` and `wm` are the older
  // sets. Without an age they were unreachable, so a 55-year-old was shown
  // rooms full of people in their twenties on the screen telling them they
  // had found their people.
  const older = age != null && age >= 40;
  if (audience === "men") return [older ? "mm" : "m"];
  if (audience === "sapphic") return [older ? "wm" : ""];
  // Mixed alternates rather than letting one community stand in for everyone.
  return older ? ["mm", "wm"] : ["m", ""];
}

/**
 * Card order for an ad's event type (`et=c` / `et=d`).
 *
 * Every pack carries both a `cafe` and a `table` shot, so this is a reordering
 * rather than a different library: whichever the ad promised leads, and the
 * other still appears further round. Someone who clicked a coffee ad and lands
 * on a wall of candlelit dinner tables has been shown the wrong product before
 * reading a word.
 */
export function storyCardOrder(eventType: string | null): string[] {
  if (eventType === "coffee") {
    return ["cafe", "talking", "pair", "outside", "group", "table", "trio", "pride"];
  }
  if (eventType === "dinner") {
    return ["table", "group", "trio", "pride", "talking", "cafe", "pair", "outside"];
  }
  return STORY_CARDS;
}

/**
 * Shots for a given audience, ad event type and age, in one call, so the
 * intro's prints, the reveal's wheel and the welcome print can't drift apart.
 *
 * `age` is optional because it isn't always known yet: the intro and the
 * reveal both run before the birthday is asked, so they get the younger sets.
 * The welcome, which runs last, has it.
 *
 * `distinctTables` is for the surfaces that caption their photos — the intro's
 * stack and the welcome's print. Both must draw from the same filtered deck,
 * or the welcome hands someone a print they watched go past two minutes ago.
 */
/** A card, with the pack it came from — captions depend on both. */
export interface StoryCard {
  name: string;
  prefix: StoryPackPrefix;
  shot: StoryShot;
}

/**
 * The same deck `storyShotsFor` returns, with each card's name and pack kept.
 *
 * The intro captions its prints, and a caption has to agree with the photo
 * underneath it in two ways: what it shows (a flag, a coffee) and whose night
 * it was (a mixed deck alternates packs, so "boys' night" belongs only on the
 * cards drawn from a men's pack).
 */
export function storyCardsFor(
  audience: QuizAudience,
  eventType: string | null,
  count?: number,
  age?: number | null,
  options: { distinctTables?: boolean } = {},
): StoryCard[] {
  const packs = packsForAudience(audience, age);
  const order = storyCardOrder(eventType);
  // Captioned surfaces ask for one card per table; the wheel takes the deck as
  // ordered, repeats and all, because nothing there claims anything about them.
  let deck = order;
  if (options.distinctTables) {
    const seen = new Set<string>();
    deck = order.filter((name) => {
      const key = tableKey(packs, name);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  const names = count ? deck.slice(0, count) : deck;
  return names.map((name, i) => {
    const prefix = packs.length === 1 ? packs[0] : packs[i % 2];
    return {
      name,
      prefix,
      shot: storyShot(prefix ? `${prefix}_${name}` : name),
    };
  });
}

export function storyShotsFor(
  audience: QuizAudience,
  eventType: string | null,
  count?: number,
  age?: number | null,
  options: { distinctTables?: boolean } = {},
): StoryShot[] {
  return storyCardsFor(audience, eventType, count, age, options).map(
    (card) => card.shot,
  );
}
